// =========================================================================
// Replacing the Ledger: what each account's key needs before it is retired
// (Screens' keys round 3, 23 Oct 2026; corrected in keys round 5, 26 Oct
// 2026). A retired key is never deleted and still signs what its addresses
// hold, so the money is moved FIRST and confirmed on chain.
//
// What the desk can and cannot do, which the procedure follows (round 5):
//   · it cannot sweep an old Ledger's addresses into a NEW Ledger's floats
//     (#1): signing is by the registered Ledger, and a new Ledger's Hot key is
//     refused while the old deposit key stands (the same-Ledger rule,
//     bff/src/routes/custody-keys.ts). So the deposits go into the OLD Hot
//     first, and Hot is emptied from there;
//   · Merchant Hot has no Move to Treasury (contracts FILL_BY_SOURCE) — it is
//     paid out (#3);
//   · Treasury's words follow how it signs, read from the register (#2);
//   · what a key holds is read from EVERY address it issued, however old
//     (GET /keys/:id/holdings) — a failed, stale or missing reading is not
//     read and never "empty" (#5; Astra round c, B5);
//   · a new Ledger's deposit key is refused until the new Ledger is the
//     operator's registered signing device (key_not_this_device) — that step
//     is in the procedure (round c); how a device is replaced is the
//     operator's decision, filed with the Command Centre.
// Pure: the words, by account and by what the readings hold.
// =========================================================================

import type { KeyHoldings, WalletBalance } from './api';
import { money } from './custody-format';
import { KEY_ACCOUNT_CLASS } from './custody-renewals';
import { poolTotal } from './custody-screen';

export type ReplacementPlan = { headline: string; lines: string[] };
type FloatRow = Pick<WalletBalance, 'class' | 'balance' | 'asset'> & Partial<Pick<WalletBalance, 'signing'>>;

/** The one line on the Retire confirm; what to do sits behind Details. */
export const REPLACEMENT_HEADLINE = 'Sweep funds before handing over the old Ledger.';
const KEEP = 'Keep the old Ledger until every wallet it signs is empty and confirmed on chain — a retired key still signs what its addresses hold.';
const NO_CROSS = 'The desk cannot yet sweep an old Ledger\'s addresses into a new Ledger\'s wallets: a payment that reaches an old address after the switch needs the old Ledger — keep it, and tell the wallet session.';

/**
 * What a key still signs, from every address it issued (GET /keys/:id/holdings): a figure only when every address
 * has a current reading; "at least" with the count not read when some have; "not read" when none has or the read is
 * not in. Never "empty" from a reading that failed or is not current (Astra round c, B5).
 */
export function keyHolding(h: Pick<KeyHoldings, 'addresses' | 'total' | 'read' | 'complete'> | null | undefined, asset = 'USDTTRC'): string {
  if (!h) return 'balance not read';
  const n = h.addresses.length;
  if (n === 0) return 'has no address';
  const across = n > 1 ? ` across ${n} addresses` : '';
  if (h.complete && h.total !== null) return `holds ${money(h.total, asset)}${across}`;
  if (h.read === 0 || h.total === null) return `balance not read (${n} address${n > 1 ? 'es' : ''})`;
  return `holds at least ${money(h.total, asset)} — ${n - h.read} of ${n} addresses not read`;
}

/** How long a holdings answer stands in the Retire confirm before it is read again (the server calls a reading current for 4 min, a retired address's for 16). */
export const HOLDINGS_CURRENT_MS = 60_000;

/** The parts of a holdings read the confirm judges by (a react-query result has them all). */
export type HoldingsRead<T> = { data?: T; isError: boolean; dataUpdatedAt: number; fetchStatus?: 'fetching' | 'paused' | 'idle' };

/**
 * The holdings the Retire confirm may speak from (Astra round d, #4): the read's own answer only while it is KNOWN —
 * read, not failed since, not waiting on a refresh that paused (offline: Astra round e #4), and younger than
 * HOLDINGS_CURRENT_MS. A cached "holds 0" after a failed or paused refresh, or an answer gone old while the confirm
 * stayed open, is not known: the plan then says "balance not read".
 */
export function holdingsForPlan<T>(q: HoldingsRead<T>, now = Date.now()): T | null {
  if (q.isError || q.data === undefined || q.fetchStatus === 'paused') return null;
  return now - q.dataUpdatedAt < HOLDINGS_CURRENT_MS ? q.data : null;
}

/**
 * When a known holdings answer stops being current — the moment an open confirm must be drawn again (round e #4: a
 * refresh that pauses offline notifies nothing, so nothing else would redraw it). Null when nothing is shown as known.
 */
export function holdingsGoOldAt<T>(q: HoldingsRead<T>): number | null {
  if (q.isError || q.data === undefined || q.fetchStatus === 'paused') return null;
  return q.dataUpdatedAt + HOLDINGS_CURRENT_MS;
}

/** How Treasury signs, by the register: its 2-of-3, this Ledger, or not read. */
export function treasurySigning(floats: ReadonlyArray<FloatRow> | null): 'multisig' | 'ledger' | 'unknown' {
  const rows = (floats ?? []).filter((w) => w.class === 'treasury');
  if (rows.length === 0 || rows.some((w) => !w.signing)) return 'unknown';
  if (rows.every((w) => w.signing === 'multisig_2of3')) return 'multisig';
  return rows.some((w) => w.signing === 'ledger') ? 'ledger' : 'unknown';
}

/** The procedure for THIS key, named by its account: where its money goes, and in what order. */
export function replacementPlan(account: number | null, floats: ReadonlyArray<FloatRow> | null, holdings: Parameters<typeof keyHolding>[0] = null): ReplacementPlan {
  const cls = account === null ? undefined : KEY_ACCOUNT_CLASS[account];
  const lines: string[] = [];
  switch (cls) {
    case 'deposit':
      lines.push(`Account 1 · Deposit ${keyHolding(holdings)}: sweep every address it issued into Hot FIRST, while Hot is still this Ledger's; retire this key last, after Hot and Merchant Hot.`);
      lines.push(NO_CROSS);
      break;
    case 'running_hot':
      lines.push(`Account 2 · Hot ${keyHolding(holdings)}: once the deposits are swept in, Move to Treasury or pay it out to approved addresses, confirmed on chain, before retiring; open cards are paid or lapse first.`);
      break;
    case 'dirty_hot':
      lines.push(`Account 3 · Merchant Hot ${keyHolding(holdings)}: pay it out to the merchants' approved addresses, confirmed on chain, before retiring — the desk has no Move to Treasury from Merchant Hot.`);
      break;
    case 'treasury': {
      const how = treasurySigning(floats);
      lines.push(how === 'multisig'
        ? `Account 4 · Treasury ${keyHolding(holdings)} is Treasury's 2-of-3: this key is replaced by Treasury's own procedure, not here.`
        : how === 'ledger'
          ? `Account 4 · Treasury ${keyHolding(holdings)} is signed by this Ledger: the desk cannot yet move it to a new Ledger's Treasury — keep this key and the old Ledger, and tell the wallet session.`
          : `Account 4 · Treasury: how it signs was not read — retire this key only once nothing it signs holds money.`);
      break;
    }
    default:
      lines.push('This key\'s account is not known: retire it only once nothing it signs holds money.');
  }
  lines.push(KEEP);
  return { headline: REPLACEMENT_HEADLINE, lines };
}

/** The whole procedure, account by account — the "How to register" text. */
export const REPLACEMENT_ORDER: readonly string[] = [
  'Replacing the Ledger goes money first, then the keys, then the new Ledger:',
  '1. Deposit (account 1): sweep every address into Hot while Hot is still this Ledger\'s.',
  '2. Hot (account 2): Move to Treasury or pay it out. Merchant Hot (account 3): pay the merchants. Confirm each on chain.',
  '3. Retire Hot, Merchant Hot, then the deposit key.',
  '4. Have the desk register the new Ledger as your signing device — the new deposit key is refused until it is.',
  '5. Register the new Ledger\'s deposit key (account 1) first — its other keys are checked against it.',
  '6. Treasury (account 4) by how it signs: its 2-of-3 by its own procedure.',
  NO_CROSS,
  KEEP,
];
